import { statusLabel, statusColor } from '../status-presentation'
import { getIgStorySaDisplayName } from '../sa-display-name'
import '../ig-story.css'
import SalesIgStoryApi from 'api/admins/sales-ig-story-api'
import { TitlePage } from 'components/atoms'
import { ALERT_TYPES } from 'constants/alert-constants'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Collapse,
  Container,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table
} from 'reactstrap'

const propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string.isRequired
    }).isRequired
  }).isRequired,
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const imagePreviewPropTypes = {
  title: PropTypes.string.isRequired,
  src: PropTypes.string,
  alt: PropTypes.string
}


const displayValue = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean).join(', ') || '-'
  if (value === null || value === undefined) return '-'

  const text = String(value).trim()
  return text || '-'
}

const formatDateTime = (value) => {
  if (!value) return '-'

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'

  return new Intl.DateTimeFormat('id-ID', {
    dateStyle : 'medium',
    timeStyle : 'short',
    timeZone  : 'Asia/Jakarta'
  }).format(date)
}

// Explains why an assignment cycle exists without implying the product's inventory origin.
const assignmentTriggerLabel = (sourceEvent) => ({
  initial_backfill     : 'Initial Backfill',
  appraisal_approved  : 'Appraisal Approved',
  manual_owner_reupload: 'Owner Re-upload',
  manual_owner_reassign: 'Owner Reassign',
  manual_owner_swap_restore: 'Owner Swap Restore'
}[sourceEvent] || displayValue(sourceEvent))



// Reuses the simple Inventory detail preview pattern so SA can inspect product and inclusions quickly.
const ImagePreview = ({ title, src, alt }) => (
  <div className='mb-3'>
    <p className='small text-uppercase text-muted font-weight-bold mb-2'>{ title }</p>
    { src ? (
      <a href={ src } target='_blank' rel='noopener noreferrer' className='d-block text-decoration-none'>
        <div
          className='overflow-hidden rounded d-flex align-items-center justify-content-center border bg-light'
          style={{ width: '100%', height: '280px' }}
        >
          <img
            src={ src }
            alt={ alt || title }
            loading='lazy'
            decoding='async'
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        </div>
      </a>
    ) : (
      <div
        className='rounded d-flex align-items-center justify-content-center border bg-light text-muted'
        style={{ width: '100%', height: '280px' }}
      >
        Foto belum tersedia
      </div>
    ) }
  </div>
)

ImagePreview.propTypes = imagePreviewPropTypes

// One assignment cycle is the unit of action, while the page stays focused on what SA needs to post.
const SalesIgStoryDetailPage = ({ history, match, pageUtils }) => {
  const assignmentId = match.params.id
  const [ viewer, setViewer ] = useState({})
  const [ assignment, setAssignment ] = useState(null)
  const [ loading, setLoading ] = useState(true)
  const [ confirming, setConfirming ] = useState(false)
  const [ confirmModalOpen, setConfirmModalOpen ] = useState(false)
  const [ assignmentOpen, setAssignmentOpen ] = useState(false)
  const [ message, setMessage ] = useState('')
  const [ errorMessage, setErrorMessage ] = useState('')

  // Reloads detail after completion so the page becomes an immutable DONE view immediately.
  const loadAssignment = useCallback(async () => {
    setLoading(true)
    setErrorMessage('')

    try {
      const response = await SalesIgStoryApi.show({ id: assignmentId })
      setAssignment(response.data.assignment || null)
      setViewer(response.data.viewer || {})
    }
    catch (error) {
      setAssignment(null)
      const text = error?.response?.data?.message || 'Gagal memuat Detail IG Story.'
      setErrorMessage(text)
      pageUtils?.setApiErrorMsg?.(error?.response?.data || error)
    }
    finally {
      setLoading(false)
    }
  }, [assignmentId, pageUtils])

  useEffect(() => {
    loadAssignment()
  }, [loadAssignment])

  // Confirmation is deliberately explicit because it changes active work into immutable DONE history.
  const confirmUploaded = async () => {
    if (!assignment?.permissions?.can_confirm_uploaded || confirming) return

    setConfirming(true)
    setMessage('')
    setErrorMessage('')

    try {
      const response = await SalesIgStoryApi.confirmUploaded({ id: assignmentId })
      setAssignment(response.data.assignment || assignment)
      setConfirmModalOpen(false)
      setMessage(response.data.message || 'IG Story berhasil dikonfirmasi sebagai uploaded.')
      pageUtils?.setAlertMsg?.(response.data.message, ALERT_TYPES.SUCCESS)
    }
    catch (error) {
      const text = error?.response?.data?.message || 'Gagal mengonfirmasi upload IG Story.'
      setErrorMessage(text)
      pageUtils?.setApiErrorMsg?.(error?.response?.data || error)
    }
    finally {
      setConfirming(false)
    }
  }

  if (loading) {
    return (
      <Container fluid className='ig-story-page'>
        <TitlePage mainTitle='Detail Product' subTitle='IG Story Sales Team' />
        <Card><CardBody className='text-center py-5'><Spinner /></CardBody></Card>
      </Container>
    )
  }

  const product = assignment?.product || {}
  const canConfirm = assignment?.permissions?.can_confirm_uploaded === true
  const returnTo = new URLSearchParams(history.location.search || '').get('return_to')
  const safeReturnTo = typeof returnTo === 'string' &&
    /^\/app\/sales\/ig-story\/(my-queue|audit-queue)(\?|$)/.test(returnTo)
    ? returnTo
    : (viewer.can_audit ? '/app/sales/ig-story/audit-queue' : '/app/sales/ig-story/my-queue')

  return (
    <Container fluid className='ig-story-page'>
      <TitlePage mainTitle='Detail Product' subTitle='IG Story Sales Team' />

      <div className='mb-3'>
        <Button color='secondary' outline onClick={ () => history.push(safeReturnTo) }>
          <i className='fas fa-arrow-left mr-2' /> Back to IG Story
        </Button>
      </div>

      { message && <Alert color='success' toggle={ () => setMessage('') }>{ message }</Alert> }
      { errorMessage && <Alert color='danger' toggle={ () => setErrorMessage('') }>{ errorMessage }</Alert> }

      { !assignment ? (
        <Alert color='warning'>Assignment tidak ditemukan atau tidak dapat diakses oleh akun ini.</Alert>
      ) : (
        <>
          <Card className='mb-4'>
            <CardBody>
              <div className='d-flex flex-wrap align-items-center mb-4'>
                <Badge color={ statusColor(assignment.status) } className='mr-2 mb-2'>
                  { statusLabel(assignment.status) }
                </Badge>

                { canConfirm && (
                  <Button color='success' className='mb-2' onClick={ () => setConfirmModalOpen(true) } disabled={ confirming }>
                    { confirming
                      ? <><Spinner size='sm' className='mr-2' />Saving...</>
                      : <><i className='fas fa-check mr-2' />Confirm IG Story Uploaded</> }
                  </Button>
                ) }
              </div>

              <hr className='my-4' />

              <Row>
                <Col xs='12' md='6'>
                  <ImagePreview
                    title='Foto Product'
                    src={ product.product_photo_url }
                    alt={ product.name || product.sku || 'Product' }
                  />
                </Col>
                <Col xs='12' md='6'>
                  <ImagePreview
                    title='Foto Kelengkapan'
                    src={ product.comes_with_image_url }
                    alt={ `Foto Kelengkapan ${product.name || product.sku || ''}` }
                  />
                </Col>
              </Row>

              <hr className='my-4' />

              <Table bordered responsive size='sm' className='mb-0'>
                <tbody>
                  <tr><th style={{ width: '34%' }}>SKU</th><td>{ displayValue(product.sku) }</td></tr>
                  <tr><th>Condition</th><td>{ displayValue(product.condition_name) }</td></tr>
                  <tr><th>Product Name</th><td>{ displayValue(product.name) }</td></tr>
                  <tr><th>Comes With</th><td>{ displayValue(product.comes_with) }</td></tr>
                  <tr><th>Consignor Name</th><td>{ displayValue(product.consignor_seller_name) }</td></tr>
                  <tr><th>Product Curator</th><td>{ displayValue(product.product_curator_pic) }</td></tr>
                  <tr><th>Count</th><td>{ assignment.upload_count || 0 }</td></tr>
                  <tr><th>Internal Category</th><td>{ displayValue(assignment.internal_category) }</td></tr>
                </tbody>
              </Table>

              <hr className='my-4' />

              <div
                className='d-flex justify-content-between align-items-center'
                style={{ cursor: 'pointer' }}
                role='button'
                tabIndex='0'
                aria-expanded={ assignmentOpen }
                onClick={ () => setAssignmentOpen(!assignmentOpen) }
                onKeyDown={ (event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    setAssignmentOpen(!assignmentOpen)
                  }
                } }
              >
                <h3 className='mb-0'>Assignment</h3>
                <i className={ `fas fa-chevron-${assignmentOpen ? 'up' : 'down'} text-muted` } />
              </div>

              <Collapse isOpen={ assignmentOpen }>
                <Table bordered responsive size='sm' className='mt-3 mb-0'>
                  <tbody>
                    <tr><th style={{ width: '34%' }}>Assignment ID</th><td>#{ assignment.id }</td></tr>
                    <tr><th>Assigned To</th><td>{ displayValue(getIgStorySaDisplayName(assignment.assignee)) }</td></tr>
                    <tr><th>Assignment Trigger</th><td>{ assignmentTriggerLabel(assignment.source_event) }</td></tr>
                    <tr><th>Assigned At</th><td>{ formatDateTime(assignment.assigned_at) }</td></tr>
                    <tr>
                      <th>Status</th>
                      <td><Badge color={ statusColor(assignment.status) }>{ statusLabel(assignment.status) }</Badge></td>
                    </tr>
                    <tr><th>Completed At</th><td>{ formatDateTime(assignment.completed_at) }</td></tr>
                    <tr><th>Replaced At</th><td>{ formatDateTime(assignment.replaced_at) }</td></tr>
                    <tr><th>Replaced by SKU</th><td>{ displayValue(assignment.replaced_by?.sku) }</td></tr>
                  </tbody>
                </Table>
              </Collapse>
            </CardBody>
          </Card>
          { viewer.can_view_audit_logs && assignment.tracking && (
            <Card className='mb-4'><CardBody>
              <h3>Assignment Audit Log</h3>
              <Table bordered responsive size='sm'>
                <thead><tr><th>Date</th><th>Activity</th><th>Actor</th><th>Detail</th></tr></thead>
                <tbody>
                  { (assignment.tracking.audit_logs || []).length === 0 && <tr><td colSpan='4'>Belum ada audit log.</td></tr> }
                  { (assignment.tracking.audit_logs || []).map((entry) => <tr key={ entry.id }>
                    <td>{ formatDateTime(entry.created_at) }</td><td>{ entry.activity }</td><td>{ entry.done_by || '-' }</td>
                    <td><details><summary>Before / After</summary><pre style={{ whiteSpace: 'pre-wrap' }}>{ JSON.stringify({ before: entry.before, after: entry.after }, null, 2) }</pre></details></td>
                  </tr>) }
                </tbody>
              </Table>
            </CardBody></Card>
          ) }
        </>
      ) }

      <Modal
        isOpen={ confirmModalOpen }
        toggle={ () => { if (!confirming) setConfirmModalOpen(false) } }
        centered
      >
        <ModalHeader toggle={ confirming ? undefined : () => setConfirmModalOpen(false) }>
          Konfirmasi Upload IG Story
        </ModalHeader>
        <ModalBody>
          <p className='mb-2'>Pastikan product ini sudah berhasil di-upload ke IG Story.</p>
          <p className='text-muted mb-0'>Konfirmasi mencatat penyelesaian upload dan menambah Count. Status tetap ASSIGNED.</p>
        </ModalBody>
        <ModalFooter>
          <Button color='secondary' outline onClick={ () => setConfirmModalOpen(false) } disabled={ confirming }>
            Batal
          </Button>
          <Button color='success' onClick={ confirmUploaded } disabled={ confirming }>
            { confirming
              ? <><Spinner size='sm' className='mr-2' />Saving...</>
              : <><i className='fas fa-check mr-2' />Ya, Konfirmasi</> }
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  )
}

SalesIgStoryDetailPage.propTypes = propTypes

export default SalesIgStoryDetailPage
